"use client";

import clsx from "clsx";
import React, {forwardRef, useCallback, useLayoutEffect, useRef, useState} from "react";
import {adjustTextareaHeight} from "./constants";
import {splitSlashCommand} from "@/utils/chat/slashCommand";

interface HighlightedTextareaProps
   extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "value"> {
   value: string;
   className?: string;
   wrapperClassName?: string;
   isSlashMenuOpen?: boolean;
   skillSlugs?: readonly string[];
   maxRows?: number;
}

// Native text remains visible; the overlay only colours the slash token.
const HighlightedTextarea = forwardRef<HTMLTextAreaElement, HighlightedTextareaProps>(
   ({value, className, wrapperClassName, isSlashMenuOpen = false, skillSlugs = [], maxRows = 12, onScroll, onKeyDown, onSelect, style, ...rest}, ref) => {
      const backdropRef = useRef<HTMLDivElement>(null);
      const textareaRef = useRef<HTMLTextAreaElement | null>(null);
      const [tokenSelected, setTokenSelected] = useState(false);
      const candidate = splitSlashCommand(value);
      const split = candidate && skillSlugs.includes(candidate.token.slice(1)) && (!candidate.after || /^\s/.test(candidate.after)) ? candidate : null;
      const tokenStart = split ? split.before.length : 0;
      const tokenEnd = split ? tokenStart + split.token.length : 0;

      const isTokenSelection = (el: HTMLTextAreaElement) =>
         !!split && el.selectionStart === tokenStart && el.selectionEnd === tokenEnd;

      // Stable callback so a re-render does not reattach the ref. Both the
      // mobile and desktop fields share one ref, and slash completion points
      // that ref at whichever field was used.
      const setTextareaRef = useCallback((node: HTMLTextAreaElement | null) => {
         textareaRef.current = node;
         if (typeof ref === "function") ref(node);
         else if (ref) ref.current = node;
      }, [ref]);

      // A scrollbar narrows the field but not the mirror, so a click that looks
      // like it is inside a word actually hits the end of the draft.
      const syncBackdrop = useCallback(() => {
         const textarea = textareaRef.current;
         const backdrop = backdropRef.current;
         if (!textarea || !backdrop) return;
         const computed = getComputedStyle(textarea);
         const border = (parseFloat(computed.borderLeftWidth) || 0) + (parseFloat(computed.borderRightWidth) || 0);
         const track = textarea.offsetWidth - textarea.clientWidth - border;
         for (const property of ["font", "letter-spacing", "line-height", "text-indent", "tab-size", "padding", "border", "box-sizing"]) {
            backdrop.style.setProperty(property, computed.getPropertyValue(property));
         }
         backdrop.style.width = track > 0.5 ? `calc(100% - ${track}px)` : "";
         backdrop.scrollTop = textarea.scrollTop;
         backdrop.scrollLeft = textarea.scrollLeft;
      }, []);

      const resizeAndSync = useCallback(() => {
         if (textareaRef.current) adjustTextareaHeight(textareaRef.current, maxRows);
         syncBackdrop();
      }, [maxRows, syncBackdrop]);

      // Height and scrollbar width must settle before the overlay is painted.
      // Measuring forces a layout, and the chat re-renders the composer on
      // every streamed chunk, so only rerun when an input to the height
      // changes. Expanding swaps the field's classes, so className covers it.
      useLayoutEffect(() => {
         resizeAndSync();
      }, [resizeAndSync, value, className, style, rest.placeholder, rest.rows]);

      useLayoutEffect(() => {
         const textarea = textareaRef.current;
         if (!textarea || typeof ResizeObserver === "undefined") return;
         let frame = 0;
         let width = textarea.offsetWidth;
         // Writing the height inside the observer callback resizes the observed
         // field again, which the browser reports as a ResizeObserver loop error.
         const schedule = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(resizeAndSync);
         };
         // Our own height writes land here too. Only a width change can rewrap
         // the draft. Window resizes stay separate: the dvh cap can change
         // without the width changing.
         const observer = new ResizeObserver(() => {
            if (textarea.offsetWidth === width) return;
            width = textarea.offsetWidth;
            schedule();
         });
         observer.observe(textarea);
         window.addEventListener("resize", schedule);
         return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
            window.removeEventListener("resize", schedule);
         };
      }, [resizeAndSync]);

      // Select the token on the first delete so the next keypress removes it atomically.
      const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
         const isDelete = e.key === "Backspace" || e.key === "Delete";
         const hasModifier = e.metaKey || e.ctrlKey || e.altKey || e.shiftKey;
         const el = e.currentTarget;
         if (isDelete && !isSlashMenuOpen && !hasModifier && split && el.selectionStart === el.selectionEnd) {
            const caret = el.selectionStart;
            const atTrailingEdge = e.key === "Backspace" && caret === tokenEnd;
            const atLeadingEdge = e.key === "Delete" && caret === tokenStart;
            if (atTrailingEdge || atLeadingEdge) {
               e.preventDefault();
               el.setSelectionRange(tokenStart, tokenEnd);
               setTokenSelected(true);
               return;
            }
         }
         onKeyDown?.(e);
      };

      return (
         // Callers put bottom padding on this outer box. Bottom padding inside
         // a height-capped field shows a clipped sliver of the next row.
         <div className={wrapperClassName}>
            <div className="relative">
               <div
                  ref={backdropRef}
                  aria-hidden="true"
                  style={{color: "transparent", backgroundColor: "transparent"}}
                  className={clsx(
                     className,
                     "pointer-events-none absolute inset-0 z-[1] select-none overflow-hidden whitespace-pre-wrap break-words"
                  )}
               >
                  {split ? (
                     <>
                        {split.before}
                        {/* Paint selection without padding or margins that alter line layout. */}
                        {tokenSelected ? (
                           <span className="rounded-md bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300">
                              {split.token}
                           </span>
                        ) : (
                           <span className="text-blue-600 dark:text-blue-300">{split.token}</span>
                        )}
                        {split.after}
                        {/* Preserve the final line without adding a second blank line. */}
                        {value.endsWith("\n") ? "\u200b" : "\n"}
                     </>
                  ) : null}
               </div>
               <textarea
                  ref={setTextareaRef}
                  value={value}
                  onKeyDown={handleKeyDown}
                  onSelect={(e) => {
                     setTokenSelected(isTokenSelection(e.currentTarget));
                     onSelect?.(e);
                  }}
                  onScroll={(e) => {
                     syncBackdrop();
                     onScroll?.(e);
                  }}
                  className={clsx(
                     className,
                     "relative block bg-transparent whitespace-pre-wrap break-words",
                     split && tokenSelected && "selection:bg-transparent selection:text-transparent"
                  )}
                  {...rest}
                  style={style}
               />
            </div>
         </div>
      );
   }
);

HighlightedTextarea.displayName = "HighlightedTextarea";

export default HighlightedTextarea;
